import { b64urlDecode } from "../util/base64";
import type { ReleaseKey } from "./release-keys";

/** TypeScript mirror of installer/schemas/image-manifest.schema.json (v1). */
export interface ManifestFile {
  file: string;
  sha256: string;
  size: number;
}
export interface ManifestImage extends ManifestFile {
  partition: string;
  source: "quickhub" | "google";
  flash_via?: "bootloader" | "fastbootd";
  sparse?: boolean;
}
export interface GoogleImage extends ManifestFile {
  partition: "bootloader" | "radio";
  source: "google";
  google_sha256: string;
  version: string;
}
export interface ImageManifest {
  schema_version: 1;
  model: string;
  codename: string;
  build_id: string;
  android: string;
  channel: "quickhub" | "stock";
  created_at?: string;
  release_notes_url?: string;
  anti_rollback?: number;
  images: ManifestImage[];
  bootloader: GoogleImage;
  radio: GoogleImage;
  avb_custom_key: ManifestFile | null;
  erase?: string[];
  google_factory?: { build_id: string; url: string; sha256: string };
  signature_alg: "ES256";
}

/** What `GET /installer/images/{model}/manifest` returns (see docs/cloud-api-contract.md). */
export interface SignedManifestEnvelope {
  /** base64url of the exact manifest.json bytes that were signed. */
  manifest: string;
  /** base64url of the raw 64-byte ES256 signature (r||s) over those bytes. */
  signature: string;
  key_id: string;
  /** Absolute URL prefix; every `file` is fetched from `${base_url}${file}`. */
  base_url: string;
}

export class ManifestError extends Error {
  constructor(
    readonly code: "signature" | "unknown_key" | "shape" | "google_mismatch" | "model_mismatch",
    message: string,
  ) {
    super(message);
    this.name = "ManifestError";
  }
}

const SHA = /^[a-f0-9]{64}$/;
const FILE = /^[A-Za-z0-9._-]+$/;
const PART = /^[a-z][a-z0-9_]{0,31}$/;
const CODENAME = /^[a-z][a-z0-9]{1,15}$/;
const BUILD = /^[A-Z0-9]{2,6}\.[0-9]{6}\.[0-9]{3}(\.[A-Z0-9]+)*$/;

function fail(path: string, why: string): never {
  throw new ManifestError("shape", `manifest.${path}: ${why}`);
}
function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function str(o: Record<string, unknown>, k: string, path: string, re?: RegExp): string {
  const v = o[k];
  if (typeof v !== "string") fail(`${path}${k}`, "expected string");
  if (re && !re.test(v)) fail(`${path}${k}`, `bad format ${JSON.stringify(v)}`);
  return v;
}
function int(o: Record<string, unknown>, k: string, path: string, min: number): number {
  const v = o[k];
  if (typeof v !== "number" || !Number.isInteger(v) || v < min) fail(`${path}${k}`, `expected integer ≥ ${min}`);
  return v;
}
function onlyKeys(o: Record<string, unknown>, allowed: string[], path: string): void {
  for (const k of Object.keys(o)) if (!allowed.includes(k)) fail(`${path}${k}`, "unknown field");
}

function fileEntry(v: unknown, path: string): ManifestFile {
  if (!isObj(v)) fail(path, "expected object");
  onlyKeys(v, ["file", "sha256", "size"], `${path}.`);
  return { file: str(v, "file", `${path}.`, FILE), sha256: str(v, "sha256", `${path}.`, SHA), size: int(v, "size", `${path}.`, 1) };
}

function imageEntry(v: unknown, path: string): ManifestImage {
  if (!isObj(v)) fail(path, "expected object");
  onlyKeys(v, ["partition", "file", "sha256", "size", "source", "flash_via", "sparse"], `${path}.`);
  const source = str(v, "source", `${path}.`);
  if (source !== "quickhub" && source !== "google") fail(`${path}.source`, "must be quickhub|google");
  const img: ManifestImage = {
    partition: str(v, "partition", `${path}.`, PART),
    file: str(v, "file", `${path}.`, FILE),
    sha256: str(v, "sha256", `${path}.`, SHA),
    size: int(v, "size", `${path}.`, 1),
    source,
  };
  if (v["flash_via"] !== undefined) {
    if (v["flash_via"] !== "bootloader" && v["flash_via"] !== "fastbootd") fail(`${path}.flash_via`, "must be bootloader|fastbootd");
    img.flash_via = v["flash_via"];
  }
  if (v["sparse"] !== undefined) {
    if (typeof v["sparse"] !== "boolean") fail(`${path}.sparse`, "expected boolean");
    img.sparse = v["sparse"];
  }
  if (img.partition === "bootloader" || img.partition === "radio" || img.partition === "avb_custom_key") {
    fail(`${path}.partition`, `${img.partition} must not appear in images[]`);
  }
  return img;
}

function googleEntry(v: unknown, path: string, expect: "bootloader" | "radio"): GoogleImage {
  if (!isObj(v)) fail(path, "expected object");
  onlyKeys(v, ["partition", "file", "sha256", "size", "source", "google_sha256", "version"], `${path}.`);
  if (v["partition"] !== expect) fail(`${path}.partition`, `must be ${expect}`);
  if (v["source"] !== "google") fail(`${path}.source`, "must be google");
  const g: GoogleImage = {
    partition: expect,
    file: str(v, "file", `${path}.`, FILE),
    sha256: str(v, "sha256", `${path}.`, SHA),
    size: int(v, "size", `${path}.`, 1),
    source: "google",
    google_sha256: str(v, "google_sha256", `${path}.`, SHA),
    version: str(v, "version", `${path}.`),
  };
  // Hard rule: bootloader and radio are Google's signed images, byte for byte.
  if (g.sha256 !== g.google_sha256) {
    throw new ManifestError("google_mismatch", `${expect}: sha256 differs from Google's published sha256`);
  }
  return g;
}

/** Structural validation mirroring the JSON schema. Throws ManifestError("shape"). */
export function validateManifestShape(raw: unknown): ImageManifest {
  if (!isObj(raw)) fail("", "expected object");
  onlyKeys(
    raw,
    [
      "schema_version", "model", "codename", "build_id", "android", "channel", "created_at", "release_notes_url",
      "anti_rollback", "images", "bootloader", "radio", "avb_custom_key", "erase", "google_factory", "signature_alg",
    ],
    "",
  );
  if (raw["schema_version"] !== 1) fail("schema_version", "must be 1");
  if (raw["signature_alg"] !== "ES256") fail("signature_alg", "must be ES256");
  const channel = str(raw, "channel", "");
  if (channel !== "quickhub" && channel !== "stock") fail("channel", "must be quickhub|stock");
  if (!Array.isArray(raw["images"]) || raw["images"].length === 0) fail("images", "expected non-empty array");
  const images = raw["images"].map((v, i) => imageEntry(v, `images[${i}]`));
  const m: ImageManifest = {
    schema_version: 1,
    model: str(raw, "model", "", CODENAME),
    codename: str(raw, "codename", "", CODENAME),
    build_id: str(raw, "build_id", "", BUILD),
    android: str(raw, "android", "", /^[0-9]{1,2}$/),
    channel,
    images,
    bootloader: googleEntry(raw["bootloader"], "bootloader", "bootloader"),
    radio: googleEntry(raw["radio"], "radio", "radio"),
    avb_custom_key: raw["avb_custom_key"] === null ? null : fileEntry(raw["avb_custom_key"], "avb_custom_key"),
    signature_alg: "ES256",
  };
  if (raw["created_at"] !== undefined) m.created_at = str(raw, "created_at", "");
  if (raw["release_notes_url"] !== undefined) m.release_notes_url = str(raw, "release_notes_url", "", /^https:\/\//);
  if (raw["anti_rollback"] !== undefined) m.anti_rollback = int(raw, "anti_rollback", "", 0);
  if (raw["erase"] !== undefined) {
    if (!Array.isArray(raw["erase"]) || !raw["erase"].every((e) => typeof e === "string" && /^[a-z_]+$/.test(e))) fail("erase", "bad list");
    m.erase = raw["erase"] as string[];
  }
  if (raw["google_factory"] !== undefined) {
    const gf = raw["google_factory"];
    if (!isObj(gf)) fail("google_factory", "expected object");
    onlyKeys(gf, ["build_id", "url", "sha256"], "google_factory.");
    m.google_factory = { build_id: str(gf, "build_id", "google_factory."), url: str(gf, "url", "google_factory.", /^https:\/\//), sha256: str(gf, "sha256", "google_factory.", SHA) };
  }
  if (channel === "stock") {
    if (m.avb_custom_key !== null) fail("avb_custom_key", "must be null on the stock channel");
    if (images.some((i) => i.source !== "google")) fail("images", "stock channel images must all be source=google");
  } else if (m.avb_custom_key === null) {
    fail("avb_custom_key", "required on the quickhub channel");
  }
  const seen = new Set<string>();
  for (const i of images) {
    if (seen.has(i.partition)) fail("images", `duplicate partition ${i.partition}`);
    seen.add(i.partition);
  }
  return m;
}

export interface VerifiedManifest {
  manifest: ImageManifest;
  bytes: Uint8Array;
  keyId: string;
  baseUrl: string;
}

/**
 * Verify the detached ES256 signature with a pinned key, THEN parse + validate.
 * Nothing about the manifest is trusted before the signature checks out.
 */
export async function verifyManifestEnvelope(
  env: SignedManifestEnvelope,
  keys: readonly ReleaseKey[],
  subtle: SubtleCrypto = crypto.subtle,
): Promise<VerifiedManifest> {
  const key = keys.find((k) => k.kid === env.key_id);
  if (!key) throw new ManifestError("unknown_key", `manifest signed with unknown key id ${env.key_id}`);
  const bytes = b64urlDecode(env.manifest);
  const sig = b64urlDecode(env.signature);
  if (sig.byteLength !== 64) throw new ManifestError("signature", `ES256 signature must be 64 bytes, got ${sig.byteLength}`);
  const pub = await subtle.importKey("jwk", key.jwk, { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"]);
  const ok = await subtle.verify({ name: "ECDSA", hash: "SHA-256" }, pub, sig as BufferSource, bytes as BufferSource);
  if (!ok) throw new ManifestError("signature", "manifest signature does not verify");
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new ManifestError("shape", "manifest is not JSON");
  }
  if (!/^https:\/\/[^\s]+\/$/.test(env.base_url) && !/^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/[^\s]*\/$/.test(env.base_url)) {
    throw new ManifestError("shape", `base_url must be https and end with / (got ${env.base_url})`);
  }
  return { manifest: validateManifestShape(parsed), bytes, keyId: key.kid, baseUrl: env.base_url };
}

/** Every file referenced by the manifest, in flash order. */
export function manifestFiles(m: ImageManifest): { what: string; entry: ManifestFile }[] {
  const out: { what: string; entry: ManifestFile }[] = [
    { what: "bootloader", entry: m.bootloader },
    { what: "radio", entry: m.radio },
    ...m.images.map((i) => ({ what: i.partition, entry: i })),
  ];
  if (m.avb_custom_key) out.push({ what: "avb_custom_key", entry: m.avb_custom_key });
  return out;
}

export function totalBytes(m: ImageManifest): number {
  return manifestFiles(m).reduce((n, f) => n + f.entry.size, 0);
}
